/**
 * AOP 2027 Planning — the cost charts beside the targets (David, Oct 26 2026).
 *
 * `AopCostChart` draws the budget logic itself: each month's plan cost as its two parts stacked
 * (baseline grey, AOP drafts purple — the drafts' colour in the tree) against the budget as a
 * dashed line, so the month a department goes over budget is where the bars cross the line. It
 * runs over the plan months only (no history), as the targets table does.
 *
 * `AopDepartmentGapChart` is the Overview's: one bar per department, its plan year's cost minus its
 * budget, red over and green under, biggest overspend first. A department without both figures for
 * the whole year is named below the chart, never drawn as a 0.
 *
 * Both read figures through `aopTargetsMath.ts`, the same sums the table shows.
 */
import { useId } from 'react';
import { Chart } from 'react-chartjs-2';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import type { MonthGroup } from '../internalPlayground/OrgChartTable';
import { formatEuro } from '../../types/compensation';
import { AOP_PLAN_YEAR } from './aopColumns';
import { costChartPoints, departmentYearGaps, type TargetsMonths } from './aopTargetsMath';

// Idempotent. The generic `Chart` (mixed bar + line) does not register controllers the way the
// typed `Bar`/`Line` components do — see AbsenceTenureChart.tsx.
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, BarController, LineController, Tooltip, Legend);

const BASELINE_COLOR = '#A1A1AA'; // zinc-400
const DRAFTS_COLOR = '#9333EA'; // purple-600, the drafts' `+N` in the tree
const BUDGET_COLOR = '#DC2626'; // red-600, dashed
const OVER_COLOR = '#DC2626'; // red-600, as "Plan vs budget" over
const UNDER_COLOR = '#15803D'; // green-700, as "Plan vs budget" under

const THOUSANDS = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 });
/** "€" axis ticks in thousands to one decimal — "1,5k €", never a repeated "1k €" or a "-0k €". */
const thousands = (value: number | string) => `${THOUSANDS.format(Math.round(Number(value) / 100) / 10 || 0)}k €`;
/** "+30.000 €" / "−12.000 €" / "0 €". */
const signedEuro = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${formatEuro(Math.abs(v))}`;

/** A chart's heading, as the targets table's section headings. */
function ChartHeading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <h3 className="text-xs font-medium uppercase tracking-wide text-zinc-500">{title}</h3>
      <p className="text-xs text-zinc-400">{subtitle}</p>
    </div>
  );
}

/** Monthly plan cost (baseline + drafts) against the budget — see the module note. */
export function AopCostChart({ label, months, groups }: { label: string; months: TargetsMonths; groups: MonthGroup[] }) {
  const summaryId = useId();
  const points = costChartPoints(months, groups);
  if (!points.some((p) => p.baseline !== null || p.budget !== null)) return null;
  /** A month's plan cost, null when a part is unknown (`costChartPoints`). */
  const planOf = (i: number) => (points[i].baseline !== null && points[i].drafts !== null ? points[i].baseline! + points[i].drafts! : null);
  const summary = points.map((p, i) => `${p.label}: plan ${formatEuro(planOf(i))}, budget ${formatEuro(p.budget)}`).join('; ');
  const unknownPlan = points.filter((p) => p.baseline === null && p.budget !== null).length;
  return (
    <section aria-label={`Monthly cost against budget for ${label}`} className="rounded-md border border-zinc-200 bg-white p-3">
      <ChartHeading title="Monthly cost vs budget" subtitle="Baseline and AOP drafts stacked; the dashed line is the budget." />
      <div className="mt-2" style={{ height: 220 }}>
        <Chart
          type="bar"
          role="img"
          aria-label={`Monthly plan cost against budget for ${label}`}
          aria-describedby={summaryId}
          data={{
            labels: points.map((p) => p.label),
            datasets: [
              {
                type: 'line' as const,
                label: 'Budget',
                data: points.map((p) => p.budget),
                borderColor: BUDGET_COLOR,
                borderDash: [5, 4],
                borderWidth: 2,
                // A point per month, so a budget month between two blank ones still shows.
                pointRadius: 3,
                pointBackgroundColor: BUDGET_COLOR,
                spanGaps: false,
              },
              { type: 'bar' as const, label: 'Baseline', data: points.map((p) => p.baseline), backgroundColor: BASELINE_COLOR, stack: 'plan', maxBarThickness: 24 },
              { type: 'bar' as const, label: 'AOP drafts', data: points.map((p) => p.drafts), backgroundColor: DRAFTS_COLOR, stack: 'plan', maxBarThickness: 24, borderRadius: 4 },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { position: 'bottom', labels: { boxWidth: 10 } },
              tooltip: {
                callbacks: {
                  label: (item) => `${item.dataset.label}: ${formatEuro(item.raw as number | null)}`,
                  // The table's "= Plan cost" and "Plan vs budget" rows for the month.
                  footer: (items) => {
                    const i = items[0]?.dataIndex ?? 0;
                    const plan = planOf(i);
                    const budget = points[i].budget;
                    if (plan === null) return 'Plan: unknown';
                    return budget === null ? `Plan: ${formatEuro(plan)}` : `Plan: ${formatEuro(plan)} (${signedEuro(plan - budget)} vs budget)`;
                  },
                },
              },
            },
            scales: {
              x: { stacked: true, grid: { display: false } },
              y: { stacked: true, beginAtZero: true, ticks: { callback: thousands } },
            },
          }}
        />
      </div>
      <p id={summaryId} className="sr-only">
        {summary}
      </p>
      {unknownPlan > 0 && (
        <p className="mt-2 text-xs text-zinc-500">
          No plan bar in {unknownPlan} {unknownPlan === 1 ? 'month' : 'months'} with a budget: the plan cost is hidden or not
          complete yet (see the notes under the table).
        </p>
      )}
    </section>
  );
}

/** Each department's plan year against its budget — see the module note. */
export function AopDepartmentGapChart({
  byDepartment,
  groups,
  onSelect,
}: {
  byDepartment: Record<string, { months: TargetsMonths }>;
  groups: MonthGroup[];
  onSelect?: (label: string) => void;
}) {
  const summaryId = useId();
  const { gaps, missing } = departmentYearGaps(byDepartment, groups);
  const yy = AOP_PLAN_YEAR.slice(2);
  return (
    <section aria-label={`Plan cost against budget by department, FY '${yy}`} className="rounded-md border border-zinc-200 bg-white p-3">
      <ChartHeading
        title={`FY '${yy} plan cost vs budget, by department`}
        subtitle={onSelect ? 'Red is over budget, green under. Click a bar to open that department.' : 'Red is over budget, green under.'}
      />
      {gaps.length > 0 && (
        <div className="mt-2" style={{ height: gaps.length * 32 + 48 }}>
          <Chart
            type="bar"
            role="img"
            aria-label={`Plan cost minus budget for FY '${yy}, per department`}
            aria-describedby={summaryId}
            data={{
              labels: gaps.map((g) => g.label),
              datasets: [
                {
                  label: 'Plan vs budget',
                  data: gaps.map((g) => g.gap),
                  backgroundColor: gaps.map((g) => (g.gap > 0 ? OVER_COLOR : UNDER_COLOR)),
                  maxBarThickness: 20,
                  borderRadius: 4,
                },
              ],
            }}
            options={{
              indexAxis: 'y',
              responsive: true,
              maintainAspectRatio: false,
              onClick: onSelect ? (_event, elements) => elements[0] && onSelect(gaps[elements[0].index].label) : undefined,
              // The bars open a tab, so they say so; the tabs above reach every department by keyboard.
              onHover: onSelect
                ? (event, elements) => {
                    const canvas = event.native?.target as HTMLElement | undefined;
                    if (canvas) canvas.style.cursor = elements.length ? 'pointer' : 'default';
                  }
                : undefined,
              plugins: {
                legend: { display: false },
                tooltip: { callbacks: { label: (item) => `${signedEuro(item.raw as number)} vs budget` } },
              },
              scales: { x: { ticks: { callback: thousands } }, y: { grid: { display: false } } },
            }}
          />
          <p id={summaryId} className="sr-only">
            {gaps.map((g) => `${g.label}: ${signedEuro(g.gap)}`).join('; ')}
          </p>
        </div>
      )}
      {missing.length > 0 && (
        <p className="mt-2 text-xs text-zinc-500">
          Not shown, for want of a full year&apos;s budget or plan cost: {missing.join(', ')}.
        </p>
      )}
    </section>
  );
}
